import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { MsgEditor } from '../MsgEditor/MsgEditor';
import { RmButton, RmFooter } from '../RemindersEditor/EditorShell';
import { hasUnknown, tokenPlain } from '../../msg/tokens';
import type { MsgMenuEntry, MsgToken } from '../../msg/tokens';
import {
  AR_AUDIENCE,
  AR_CALL_ALWAYS,
  AR_CALL_NEVER,
  AR_CALL_NEXT,
  AR_FIELDS,
  AR_READBACK,
  AR_STARTING_POINTS,
} from '../../ar/mockData';
import type { ArFieldKey, ArFollowUpDraft, ArStep, ArStepChannel, ArWhoGets } from '../../ar/types';

export type ArBuilderPage = 'audience' | 'messages' | 'test';

export interface ArFollowUpBuilderProps {
  page: ArBuilderPage;
  draft: ArFollowUpDraft;
  onChange: (next: ArFollowUpDraft) => void;
  onGoTo: (page: ArBuilderPage) => void;
  onCancel: () => void;
  onSaveDraft: () => void;
  onStart: () => void;
}

const STEPS: { id: ArBuilderPage; n: number; label: string }[] = [
  { id: 'audience', n: 1, label: 'Audience' },
  { id: 'messages', n: 2, label: 'Messages' },
  { id: 'test', n: 3, label: 'Test and start' },
];

const CHANNEL_LABEL: Record<ArStepChannel, string> = { text: 'Text', email: 'Email', ai_call: 'AI call', human_call: 'Human call' };
const CHANNEL_ICON: Record<ArStepChannel, 'chat' | 'mail' | 'phone-out' | 'phone'> = { text: 'chat', email: 'mail', ai_call: 'phone-out', human_call: 'phone' };

/**
 * The follow-up builder (amendment 28 §4) — **Recall's builder, three pages**,
 * with: no goal cards, four channels (text · email · AI call · human call),
 * **every text and email carries a payment link**, Reminders' *who gets it* +
 * two versions, and an AI-call step written only by ELVA, with the over-$1,000
 * and consent boxes.
 */
export function ArFollowUpBuilder({ page, draft, onChange, onGoTo, onCancel, onSaveDraft, onStart }: ArFollowUpBuilderProps) {
  return (
    <div className="cal-fade mx-auto min-h-0 w-full max-w-[1180px] flex-1 overflow-y-auto px-8 pb-16 pt-6">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
        Collections › Follow-ups › {draft.name || 'New follow-up'}
      </div>
      <Stepper active={page} onGoTo={onGoTo} />

      {page === 'audience' ? (
        <AudiencePage draft={draft} onChange={onChange} onCancel={onCancel} onContinue={() => { onGoTo('messages'); }} />
      ) : page === 'messages' ? (
        <MessagesPage draft={draft} onChange={onChange} onBack={() => { onGoTo('audience'); }} onSaveDraft={onSaveDraft} onContinue={() => { onGoTo('test'); }} />
      ) : (
        <TestPage onBack={() => { onGoTo('messages'); }} onStart={onStart} />
      )}
    </div>
  );
}

function Stepper({ active, onGoTo }: { active: ArBuilderPage; onGoTo: (p: ArBuilderPage) => void }) {
  const activeIndex = STEPS.findIndex((s) => s.id === active);
  return (
    <>
      <h1 className="m-0 mt-1 text-[26px] font-bold leading-[1.15] tracking-[-0.025em] text-[var(--text-primary)]">
        {active === 'audience' ? (<>Who is this <span className="text-[var(--brand-indigo)]">for</span>?</>) : active === 'messages' ? (<>What do they <span className="text-[var(--brand-indigo)]">get</span>?</>) : (<>Check it, then <span className="text-[var(--brand-indigo)]">start</span></>)}
      </h1>
      <div className="mt-4 flex items-center gap-2.5">
        {STEPS.map((s, i) => {
          const done = i < activeIndex;
          const on = s.id === active;
          return (
            <React.Fragment key={s.id}>
              <button type="button" disabled={on} onClick={() => { onGoTo(s.id); }} className={cn('inline-flex items-center gap-1.5', !on && 'cursor-pointer hover:underline')}>
                <span className={cn('inline-flex h-[22px] w-[22px] items-center justify-center rounded-full font-[var(--font-mono)] text-[12.5px] font-bold', on || done ? 'bg-[var(--brand-indigo)] text-white' : 'bg-[#F1F3F9] text-[var(--text-muted)]')}>
                  {done ? <Icon kind="check" size={12} stroke={3} /> : s.n}
                </span>
                <span className={cn('text-[12.5px]', on ? 'font-bold text-[var(--text-primary)]' : 'text-[var(--text-muted)]')}>{s.label}</span>
              </button>
              {i < STEPS.length - 1 ? <span className="h-px w-8 bg-[var(--border-default)]" /> : null}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
}

/* ---- audience ------------------------------------------------------------ */

function AudiencePage({ draft, onChange, onCancel, onContinue }: { draft: ArFollowUpDraft; onChange: (d: ArFollowUpDraft) => void; onCancel: () => void; onContinue: () => void }) {
  const [built, setBuilt] = useState(draft.description.trim() !== '');
  const [order, setOrder] = useState<'first' | 'last'>('first');
  const r = AR_READBACK;
  const leftOut = draft.description.toLowerCase().includes('good payer') || draft.description.toLowerCase().includes('afford');

  return (
    <>
      <p className="m-0 mt-1.5 max-w-[720px] text-[13px] leading-[1.6] text-[var(--text-muted)]">
        Describe the balances. ELVA reads it back so you can check.
      </p>

      <div className="mt-4 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] px-5 py-4">
        <label htmlFor="ar-name" className="block text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Name</label>
        <input id="ar-name" value={draft.name} placeholder="Insured, $100–$1,000, over 60 days" onChange={(e) => { onChange({ ...draft, name: e.target.value }); }}
          className="mt-1 w-full border-0 border-b border-[#dfe4ee] bg-transparent pb-1 text-[18px] font-bold tracking-[-0.02em] text-[var(--text-primary)] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]" />
      </div>

      {/* starting points */}
      <div className="mt-4">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Who should get it?</div>
        <p className="m-0 mt-1 text-[11.5px] text-[var(--text-muted)]">Pick a starting point or describe the balances in your own words.</p>
        <div className="mt-2.5 flex flex-wrap gap-2">
          {AR_STARTING_POINTS.map((sp) => {
            const on = draft.description === sp.description;
            return (
              <button key={sp.id} type="button" onClick={() => { onChange({ ...draft, description: sp.description, name: draft.name || sp.name }); setBuilt(true); }}
                className={cn('w-[210px] rounded-[12px] border p-3 text-left', on ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]' : 'border-[var(--border-default)] bg-[var(--surface-card)] hover:border-[#D6DBEA]')}>
                <span className="flex items-center justify-between">
                  <span className="text-[12.5px] font-semibold text-[var(--text-primary)]">{sp.name}</span>
                  {on ? <Icon kind="check" size={13} stroke={3} className="text-[var(--brand-indigo)]" /> : null}
                </span>
                <span className="mt-0.5 block text-[11px] text-[var(--text-muted)]">{sp.fromBalances === true ? 'From Balances › By size' : sp.sub}</span>
                <span className="mt-1 block font-[var(--font-mono)] text-[12px] font-bold text-[var(--text-primary)]">{sp.patients} patients</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* describe */}
      <div className="mt-4 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Describe the patients</div>
        <textarea value={draft.description} onChange={(e) => { onChange({ ...draft, description: e.target.value }); setBuilt(false); }} rows={2}
          className="mt-1.5 w-full rounded-[10px] border border-[#dfe4ee] px-3.5 py-2.5 text-[13.5px] leading-[1.6] text-[var(--text-primary)] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]" />
        <button type="button" onClick={() => { setBuilt(true); }} className="mt-2 rounded-[9px] bg-[var(--brand-indigo)] px-3 py-1.5 text-[12.5px] font-semibold text-white">Build the list</button>
        <div className="mt-1.5 text-[11px] leading-[1.55] text-[var(--text-muted)]">
          You can use: <b className="font-semibold text-[var(--text-secondary)]">balance</b>, how old it is, what it’s for, last payment, insurance, provider, contacted before, opened the payment link.
        </div>

        {built ? (
          <div className="mt-3 rounded-[10px] bg-[#E8F3EC] p-3">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--money-green)]">ELVA understood this as</div>
            <div className="mt-1.5 flex flex-col gap-1 text-[12px] text-[var(--text-secondary)]">
              {r.understood.map((u) => (<span key={u.label}><b className="font-semibold text-[var(--text-primary)]">{u.label}:</b> {u.value}</span>))}
            </div>
            {leftOut ? (
              <div className="mt-2 rounded-[8px] bg-[#FBF3E4] px-3 py-2 text-[11.5px] leading-[1.55] text-[var(--text-secondary)]">
                <b className="font-semibold text-[var(--warn-amber)]">Left out: “{r.leftOut.phrase}”.</b> {r.leftOut.reason}
              </div>
            ) : null}
            <div className="mt-2 border-t border-[color-mix(in_srgb,var(--money-green)_25%,transparent)] pt-2">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">Always applied</div>
              <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 max-[700px]:grid-cols-1">
                {r.alwaysApplied.map((a) => (
                  <span key={a} className="inline-flex items-start gap-1.5 text-[11.5px] text-[var(--text-secondary)]">
                    <Icon kind="lock" size={10} stroke={2.2} className="mt-0.5 shrink-0 text-[var(--text-muted)]" /> {a}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {built ? (
        <>
          <div className="mt-3 text-[13px] text-[var(--text-secondary)]">
            <b className="font-[var(--font-mono)] font-bold text-[var(--text-primary)]">{r.match}</b> match · {r.matchValue}
          </div>
          <div className="mt-1 text-[11.5px] leading-[1.55] text-[var(--text-muted)]">
            Where they are now: <b className="font-semibold text-[var(--text-secondary)]">{r.whereFirst}</b> · <b className="font-semibold text-[var(--text-secondary)]">{r.whereSecond}</b>. A patient is in one follow-up at a time.
          </div>

          {/* put first / put last */}
          <div className="mt-3 flex flex-wrap gap-2">
            <OrderRadio on={order === 'first'} title="Put it first" sub={`all ${String(r.match)} move here`} onClick={() => { setOrder('first'); }} />
            <OrderRadio on={order === 'last'} title="Put it last" sub="only patients no other follow-up takes (none today)" onClick={() => { setOrder('last'); }} />
          </div>

          {/* the patients */}
          <div className="mt-3 flex items-baseline justify-between">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">The patients</div>
            <button type="button" className="text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline">See all {r.match} →</button>
          </div>
          <div className="mt-2 overflow-hidden rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)]">
            <div className="grid grid-cols-[1.4fr_1.3fr_0.7fr_0.7fr_1fr_1.2fr] gap-x-3 border-b border-[var(--border-default)] px-4 py-2 font-[var(--font-mono)] text-[9.5px] font-bold uppercase tracking-[0.07em] text-[var(--text-muted)]">
              <span>Patient · insurance</span><span>What it’s for</span><span className="text-right">Balance</span><span className="text-right">Age</span><span>Payment link</span><span />
            </div>
            {AR_AUDIENCE.map((p) => (
              <div key={p.id} className="grid grid-cols-[1.4fr_1.3fr_0.7fr_0.7fr_1fr_1.2fr] items-center gap-x-3 border-b border-[var(--border-default)] px-4 py-2 text-[12px] last:border-0">
                <span className="min-w-0"><span className="block truncate font-semibold text-[var(--text-primary)]">{p.name}</span><span className="block text-[11px] text-[var(--text-muted)]">{p.age} · {p.insurance}</span></span>
                <span className="min-w-0 truncate text-[var(--text-secondary)]">{p.whatFor}</span>
                <span className="text-right font-[var(--font-mono)] text-[var(--text-secondary)]">{p.balance}</span>
                <span className="text-right text-[var(--text-muted)]">{p.ageDays}</span>
                <span className="text-[11px] text-[var(--text-muted)]">{p.linkStatus}</span>
                <span className="flex items-center justify-end gap-2">
                  <span className="text-[11px] font-semibold" style={{ color: p.status === 'will-get-it' ? 'var(--money-green)' : 'var(--warn-amber)' }}>● {p.statusLabel}</span>
                  <button type="button" className="text-[11px] font-semibold text-[var(--brand-indigo)] hover:underline">Leave out</button>
                </span>
              </div>
            ))}
          </div>
        </>
      ) : null}

      <RmFooter left={<span>Saved as a draft as you go.</span>}>
        <RmButton onClick={onCancel}>Cancel</RmButton>
        <RmButton kind="primary" onClick={onContinue}>Continue to messages</RmButton>
      </RmFooter>
    </>
  );
}

function OrderRadio({ on, title, sub, onClick }: { on: boolean; title: string; sub: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className={cn('flex-1 rounded-[11px] border px-3.5 py-2.5 text-left', on ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]' : 'border-[var(--border-default)] bg-[var(--surface-card)] hover:border-[#D6DBEA]')}>
      <span className="flex items-center gap-2">
        <span className={cn('inline-flex h-[15px] w-[15px] items-center justify-center rounded-full border', on ? 'border-[var(--brand-indigo)]' : 'border-[var(--border-strong)]')}>
          {on ? <span className="h-[8px] w-[8px] rounded-full bg-[var(--brand-indigo)]" /> : null}
        </span>
        <span className="text-[12.5px] font-semibold text-[var(--text-primary)]">{title}</span>
        <span className="text-[11.5px] text-[var(--text-muted)]">— {sub}</span>
      </span>
    </button>
  );
}

/* ---- messages ------------------------------------------------------------ */

function MessagesPage({ draft, onChange, onBack, onSaveDraft, onContinue }: { draft: ArFollowUpDraft; onChange: (d: ArFollowUpDraft) => void; onBack: () => void; onSaveDraft: () => void; onContinue: () => void }) {
  const [selectedId, setSelectedId] = useState<string | undefined>(draft.steps[0]?.id);
  const selected = draft.steps.find((s) => s.id === selectedId) ?? draft.steps[0];
  const issues = arIssues(draft.steps);

  const setSteps = (steps: ArStep[]) => { onChange({ ...draft, steps }); };
  const addStep = () => {
    const last = draft.steps[draft.steps.length - 1];
    const step: ArStep = { id: `s${String(Date.now())}`, channel: 'text', day: (last?.day ?? 0) + 7, mode: 'template', whoGets: 'everyone', body: [] };
    setSteps([...draft.steps, step]);
    setSelectedId(step.id);
  };

  return (
    <>
      <p className="m-0 mt-1.5 max-w-[760px] text-[13px] leading-[1.6] text-[var(--text-muted)]">
        Each step is a text, an email, an AI call, or a call by a person. Every text and email carries the payment link.
      </p>

      <div className="mt-5 flex items-start gap-5 max-[1100px]:flex-col">
        {/* steps column */}
        <div className="w-[360px] shrink-0 max-[1100px]:w-full">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">The steps</div>
          <div className="mt-2.5 flex flex-col gap-2">
            {draft.steps.map((s) => (
              <button key={s.id} type="button" onClick={() => { setSelectedId(s.id); }}
                className={cn('rounded-[12px] border p-3 text-left', s.id === selected?.id ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]' : 'border-[var(--border-default)] bg-[var(--surface-card)] hover:border-[#D6DBEA]')}>
                <span className="flex flex-wrap items-center gap-1.5">
                  <Icon kind={CHANNEL_ICON[s.channel]} size={12} stroke={2} className="text-[var(--text-muted)]" />
                  <span className="text-[12.5px] font-bold text-[var(--text-primary)]">{CHANNEL_LABEL[s.channel]}</span>
                  <span className="font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">day {s.day}</span>
                  {s.twoVersions === true ? <span className="rounded-[5px] bg-[var(--brand-indigo-tint)] px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.05em] text-[var(--brand-indigo)]">2 versions</span> : null}
                  {s.whoGets === 'not_opened' ? <span className="rounded-[5px] bg-[#FBF3E4] px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.05em] text-[var(--warn-amber)]">Didn’t open the link</span> : null}
                </span>
                <span className="mt-1 block truncate text-[11.5px] text-[var(--text-secondary)]">
                  {s.channel === 'ai_call' || s.channel === 'human_call'
                    ? (s.cardNote ?? 'A call')
                    : (tokenPlain(s.body ?? [], (k) => AR_FIELDS.find((f) => f.key === k)?.label ?? k) || 'No message yet')}
                </span>
                {issues.some((x) => x.stepId === s.id) ? (
                  <span className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-[var(--alert-red)]"><span className="h-[6px] w-[6px] rounded-full bg-[var(--alert-red)]" />{issues.find((x) => x.stepId === s.id)?.short}</span>
                ) : null}
              </button>
            ))}
          </div>
          <button type="button" onClick={addStep} className="mt-2 w-full rounded-[12px] border border-dashed border-[var(--brand-indigo)] px-3 py-2.5 text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:bg-[var(--brand-indigo-tint)]">
            + Add a step
          </button>
          <p className="m-0 mt-3 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
            <b className="font-semibold text-[var(--text-secondary)]">Paying ends it.</b> So does a reply (a person picks it up), a dispute, or an opt-out. Over $1,000, a person makes the call instead of ELVA.
          </p>
        </div>

        {/* editor */}
        <div className="min-w-0 flex-1">
          {selected === undefined ? null : (
            <ArStepEditor
              step={selected}
              onChange={(next) => { setSteps(draft.steps.map((s) => (s.id === next.id ? next : s))); }}
              onRemove={() => { const rest = draft.steps.filter((s) => s.id !== selected.id); setSteps(rest); setSelectedId(rest[0]?.id); }}
            />
          )}
        </div>
      </div>

      <RmFooter
        left={
          issues.length === 0 ? (
            <span className="text-[12.5px] font-semibold text-[var(--money-green)]">Every step is ready.</span>
          ) : (
            <span className="flex flex-col gap-1">
              <span className="text-[12.5px] font-semibold text-[var(--text-muted)]">{issues.length} thing{issues.length === 1 ? '' : 's'} to fix before you can start:</span>
              {issues.map((x, i) => (
                <button key={`${x.stepId}-${String(i)}`} type="button" onClick={() => { setSelectedId(x.stepId); }} className="text-left text-[12.5px] font-semibold text-[var(--alert-red)] hover:underline">{x.long} →</button>
              ))}
            </span>
          )
        }
      >
        <RmButton onClick={onBack}>Back</RmButton>
        <RmButton onClick={onSaveDraft}>Save as draft</RmButton>
        <RmButton kind="primary" disabled={issues.length > 0} title={issues.length > 0 ? 'Add the payment link to every text and email first' : undefined} onClick={onContinue}>Continue to test and start</RmButton>
      </RmFooter>
    </>
  );
}

function ArStepEditor({ step, onChange, onRemove }: { step: ArStep; onChange: (s: ArStep) => void; onRemove: () => void }) {
  const isCall = step.channel === 'ai_call' || step.channel === 'human_call';
  return (
    <div className="rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[14px] font-bold text-[var(--text-primary)]">{CHANNEL_LABEL[step.channel]} · day {step.day}</div>
          <div className="mt-0.5 text-[11.5px] text-[var(--text-muted)]">Day {step.day} — the day a patient joins this follow-up</div>
        </div>
        <button type="button" onClick={onRemove} className="text-[12px] font-semibold text-[var(--alert-red)] hover:underline">Remove step</button>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-6">
        <Labelled label="Channel">
          <select value={step.channel} onChange={(e) => { onChange({ ...step, channel: e.target.value as ArStepChannel }); }}
            className="h-8 rounded-[9px] border border-[#dfe4ee] bg-[var(--surface-card)] px-2 text-[12.5px] font-semibold text-[var(--text-primary)] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]">
            <option value="text">Text</option>
            <option value="email">Email</option>
            <option value="ai_call">AI call</option>
            <option value="human_call">Human call</option>
          </select>
        </Labelled>
        <Labelled label="Send on day">
          <input type="number" min={0} value={step.day} onChange={(e) => { onChange({ ...step, day: Math.max(0, Number(e.target.value)) }); }}
            className="h-8 w-[64px] rounded-[9px] border border-[#dfe4ee] px-2 text-center font-[var(--font-mono)] text-[13px] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]" />
        </Labelled>
        {step.channel === 'ai_call' ? (
          <Labelled label="Who writes it">
            <span className="inline-flex gap-1 rounded-[9px] bg-[var(--surface-sunken)] p-[3px]">
              <span className="rounded-[7px] px-2.5 py-1 text-[12px] text-[var(--text-muted)]" title="Calls are always written by ELVA">Template</span>
              <span className="rounded-[7px] bg-[var(--surface-card)] px-2.5 py-1 text-[12px] font-semibold text-[var(--text-primary)] shadow-[0_1px_2px_rgba(36,41,54,.08)]">Smart</span>
            </span>
          </Labelled>
        ) : !isCall ? (
          <Labelled label="Who writes it">
            <span className="inline-flex gap-1 rounded-[9px] bg-[var(--surface-sunken)] p-[3px]">
              {(['template', 'smart'] as const).map((m) => (
                <button key={m} type="button" onClick={() => { onChange({ ...step, mode: m }); }}
                  className={cn('rounded-[7px] px-2.5 py-1 text-[12px]', m === step.mode ? 'bg-[var(--surface-card)] font-semibold text-[var(--text-primary)] shadow-[0_1px_2px_rgba(36,41,54,.08)]' : 'text-[var(--text-secondary)]')}>
                  {m === 'smart' ? 'Smart' : 'Template'}
                </button>
              ))}
            </span>
          </Labelled>
        ) : null}
      </div>

      {step.channel === 'ai_call' ? (
        <AiCallBody step={step} onChange={onChange} />
      ) : step.channel === 'human_call' ? (
        <HumanCallBody step={step} onChange={onChange} />
      ) : (
        <TextEmailBody step={step} onChange={onChange} />
      )}
    </div>
  );
}

function TextEmailBody({ step, onChange }: { step: ArStep; onChange: (s: ArStep) => void }) {
  const menu: MsgMenuEntry<ArFieldKey>[] = AR_FIELDS.map((f) => ({
    field: f,
    unavailable: f.key === 'plan_line' ? 'Only when payment plans are on' : undefined,
  }));
  const [tab, setTab] = useState<'base' | 'opened'>('base');
  const missingLink = !bodyHasLink(step.body ?? []) || (step.twoVersions === true && !bodyHasLink(step.bodyOpened ?? []));

  const body = step.twoVersions === true && tab === 'opened' ? (step.bodyOpened ?? []) : (step.body ?? []);
  const setBody = (b: MsgToken<ArFieldKey>[]) => {
    if (step.twoVersions === true && tab === 'opened') onChange({ ...step, bodyOpened: b });
    else onChange({ ...step, body: b });
  };

  return (
    <>
      {/* who gets it */}
      <div className="mt-4 rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-3.5">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Who gets it</div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(['everyone', 'opened', 'not_opened'] as ArWhoGets[]).map((w) => (
            <button key={w} type="button" onClick={() => { onChange({ ...step, whoGets: w }); }}
              className={cn('rounded-[8px] border px-2.5 py-1 text-[11.5px] font-semibold', w === step.whoGets ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]' : 'border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-secondary)]')}>
              {w === 'everyone' ? 'Everyone' : w === 'opened' ? 'Opened the link, didn’t pay' : 'Didn’t open the link'}
            </button>
          ))}
        </div>
        <label className="mt-2.5 flex items-center gap-2 text-[12px] text-[var(--text-secondary)]">
          <input type="checkbox" checked={step.twoVersions === true} onChange={(e) => { onChange({ ...step, twoVersions: e.target.checked, whoGets: 'everyone' }); }} />
          <b className="font-semibold text-[var(--text-primary)]">Different message if they opened the link</b>
        </label>
        {step.twoVersions === true ? (
          <div className="mt-1 text-[11px] leading-[1.5] text-[var(--text-muted)]">Opened and didn’t pay: explain the bill. Didn’t open: same ask, new channel.</div>
        ) : null}
      </div>

      {/* two-version tabs */}
      {step.twoVersions === true ? (
        <div className="mt-3 flex gap-1 border-b border-[var(--border-default)]">
          {(['base', 'opened'] as const).map((tb) => (
            <button key={tb} type="button" onClick={() => { setTab(tb); }}
              className={cn('-mb-px px-3 py-1.5 text-[12px] font-semibold', tab === tb ? 'border-b-2 border-[var(--brand-indigo)] text-[var(--text-primary)]' : 'border-b-2 border-transparent text-[var(--text-muted)]')}>
              {tb === 'base' ? 'Didn’t open the link' : 'Opened the link, didn’t pay'}
            </button>
          ))}
        </div>
      ) : null}

      {step.channel === 'email' ? (
        <div className="mt-3">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Subject</div>
          <div className="mt-1.5">
            <MsgEditor<ArFieldKey> value={step.twoVersions === true && tab === 'opened' ? (step.subjectOpened ?? []) : (step.subject ?? [])} onChange={(s) => { if (step.twoVersions === true && tab === 'opened') onChange({ ...step, subjectOpened: s }); else onChange({ ...step, subject: s }); }} menu={menu} rows={1} ariaLabel="Subject" placeholder="Subject line" />
          </div>
        </div>
      ) : null}

      <div className="mt-3">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{step.channel === 'email' ? 'Body' : 'Message'}</div>
        <div className="mt-1.5">
          <MsgEditor<ArFieldKey> value={body} onChange={setBody} menu={menu} rows={3} ariaLabel="Message" placeholder="What the patient gets, with their own details filled in" />
        </div>
        {/* payment-link required */}
        {missingLink ? (
          <div className="mt-1.5 rounded-[9px] bg-[#FBF3E4] px-3 py-2 text-[11.5px] font-semibold text-[var(--warn-amber)]">
            Add <b>Payment link</b> — every text and email needs it before the step can be saved.
          </div>
        ) : null}
        <div className="mt-1.5 rounded-[9px] bg-[var(--surface-sunken)] px-3 py-2 text-[11px] leading-[1.55] text-[var(--text-muted)]">
          <b className="font-semibold text-[var(--text-secondary)]">Never, in any message:</b> a threat · the word collections · credit · a lawyer, court or agency · that treatment will be held back. ELVA adds “If this looks wrong, just reply.” to the end.
        </div>
      </div>

      {/* email preview with the ELVA Pay button */}
      {step.channel === 'email' ? (
        <div className="mt-4">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">How it reads</div>
          <div className="mt-2 grid grid-cols-2 gap-2.5 max-[1100px]:grid-cols-1">
            {AR_AUDIENCE.slice(0, 2).map((p) => (
              <div key={p.id} className="rounded-[10px] border border-[var(--border-default)] bg-[#FAFBFE] p-3">
                <div className="text-[11.5px] font-semibold text-[var(--text-primary)]">{p.name} <span className="text-[var(--text-muted)]">· {p.linkStatus.toLowerCase()}</span></div>
                <div className="mt-1 text-[11.5px] leading-[1.6] text-[var(--text-secondary)]">
                  Hi {p.name.split(' ')[0]}, here’s what the {p.balance} is for.
                </div>
                <div className="mt-2 inline-block rounded-[8px] bg-[var(--brand-indigo)] px-3 py-1.5 text-[11.5px] font-semibold text-white">Pay {p.balance} securely</div>
                <div className="mt-1.5 text-[10.5px] text-[var(--text-muted)]">If this looks wrong, just reply.</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

function AiCallBody({ step, onChange }: { step: ArStep; onChange: (s: ArStep) => void }) {
  return (
    <>
      <div className="mt-3 text-[11.5px] text-[var(--text-muted)]">Calls are always written by ELVA, from your brief.</div>

      <div className="mt-3 rounded-[10px] bg-[#FBF3E4] px-3 py-2.5 text-[11.5px] leading-[1.55] text-[var(--text-secondary)]">
        <b className="font-semibold text-[var(--warn-amber)]">Over $1,000</b> — a person makes this call instead: a task in Triage › Calls to make. <a href="#" className="font-semibold text-[var(--brand-indigo)] hover:underline">Change in Settings</a>
      </div>
      <div className="mt-2 rounded-[10px] bg-[var(--surface-sunken)] px-3 py-2.5 text-[11.5px] leading-[1.55] text-[var(--text-muted)]">
        Only patients who agreed to calls, inside your call hours (weekdays 9 am – 6 pm, from Channels). Placed by the AI Receptionist.
      </div>

      <div className="mt-3">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Brief</div>
        <div className="mt-0.5 text-[11px] text-[var(--text-muted)]">What the call should do.</div>
        <textarea value={step.brief ?? ''} onChange={(e) => { onChange({ ...step, brief: e.target.value }); }} rows={3}
          className="mt-1.5 w-full rounded-[10px] border border-[#dfe4ee] px-3.5 py-2.5 text-[12.5px] leading-[1.6] text-[var(--text-primary)] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]" />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 max-[900px]:grid-cols-1">
        <div className="rounded-[10px] border border-[color-mix(in_srgb,var(--money-green)_30%,transparent)] bg-[#E8F3EC] p-3">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--money-green)]">ELVA always</div>
          <ul className="m-0 mt-1.5 flex list-none flex-col gap-1 p-0">
            {AR_CALL_ALWAYS.map((a) => (
              <li key={a} className="flex items-start gap-1.5 text-[11.5px] leading-[1.5] text-[var(--text-secondary)]"><Icon kind="check" size={11} stroke={2.5} className="mt-0.5 shrink-0 text-[var(--money-green)]" />{a}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-3">
          <div className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">ELVA never · locked</div>
          <ul className="m-0 mt-1.5 flex list-none flex-col gap-1 p-0">
            {AR_CALL_NEVER.map((a) => (
              <li key={a} className="flex items-start gap-1.5 text-[11.5px] leading-[1.5] text-[var(--text-secondary)]"><Icon kind="lock" size={10} stroke={2.2} className="mt-0.5 shrink-0 text-[var(--text-muted)]" />{a}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">What happens next</div>
        <div className="mt-0.5 text-[11px] text-[var(--text-muted)]">ELVA writes the outcome to the Communication log.</div>
        <div className="mt-2 overflow-hidden rounded-[10px] border border-[var(--border-default)]">
          {AR_CALL_NEXT.map((c, i) => (
            <div key={c.label} className={cn('grid grid-cols-[0.8fr_1.6fr] gap-x-3 px-3 py-2 text-[11.5px]', i === 0 ? '' : 'border-t border-[var(--border-default)]')}>
              <span className="font-semibold text-[var(--text-primary)]">{c.label}</span>
              <span className="text-[var(--text-secondary)]">{c.body}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 rounded-[10px] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-3">
        <div className="text-[11.5px] font-semibold text-[var(--text-primary)]">Voicemail</div>
        <div className="mt-0.5 text-[11px] text-[var(--text-muted)]">Never the amount or what it’s for. Uses First name + Practice phone.</div>
      </div>

      <div className="mt-4 rounded-[10px] border border-[var(--border-default)] p-3">
        <div className="text-[11.5px] font-semibold text-[var(--text-primary)]">Call me as a test</div>
        <div className="mt-0.5 text-[11px] text-[var(--text-muted)]">ELVA calls you as a made-up patient, date of birth 01/01/1980.</div>
        <div className="mt-2 flex items-center gap-2">
          <input placeholder="(210) 555-0147" className="h-8 w-[160px] rounded-[9px] border border-[#dfe4ee] px-2.5 text-[12.5px] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]" />
          <RmButton kind="soft">Call me</RmButton>
        </div>
      </div>
    </>
  );
}

function HumanCallBody({ step, onChange }: { step: ArStep; onChange: (s: ArStep) => void }) {
  return (
    <div className="mt-3">
      <div className="rounded-[10px] bg-[var(--surface-sunken)] px-3 py-2.5 text-[11.5px] leading-[1.55] text-[var(--text-muted)]">
        A call by a person — this becomes a <b className="font-semibold text-[var(--text-secondary)]">task in Triage › Calls to make</b>, with everything ELVA already tried. ELVA doesn’t place it.
      </div>
      <div className="mt-3">
        <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">Note for whoever calls</div>
        <textarea value={step.brief ?? ''} onChange={(e) => { onChange({ ...step, brief: e.target.value }); }} rows={2}
          className="mt-1.5 w-full rounded-[10px] border border-[#dfe4ee] px-3.5 py-2.5 text-[12.5px] leading-[1.6] text-[var(--text-primary)] outline-none transition-[border-color,box-shadow] hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]" />
      </div>
    </div>
  );
}

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (<div><div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">{label}</div><div className="mt-1.5">{children}</div></div>);
}

function bodyHasLink(body: MsgToken<ArFieldKey>[]): boolean {
  return body.some((t) => t.kind === 'field' && t.field === 'payment_link');
}

interface ArIssue { stepId: string; short: string; long: string }
function arIssues(steps: ArStep[]): ArIssue[] {
  const out: ArIssue[] = [];
  steps.forEach((s, i) => {
    if (s.channel === 'text' || s.channel === 'email') {
      const base = bodyHasLink(s.body ?? []);
      const opened = s.twoVersions !== true || bodyHasLink(s.bodyOpened ?? []);
      if (!base || !opened) out.push({ stepId: s.id, short: 'Needs the payment link', long: `Step ${String(i + 1)} needs the payment link` });
      else if (hasUnknown(s.body ?? [], s.subject)) out.push({ stepId: s.id, short: 'Unknown field', long: `Step ${String(i + 1)} has braces that match no field` });
    }
  });
  return out;
}

/* ---- test ---------------------------------------------------------------- */

function TestPage({ onBack, onStart }: { onBack: () => void; onStart: () => void }) {
  return (
    <>
      <p className="m-0 mt-1.5 max-w-[720px] text-[13px] leading-[1.6] text-[var(--text-muted)]">
        See each step the way a patient will, send it to yourself, and start when it’s right.
      </p>
      <div className="mt-5 rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)] p-4">
        <div className="text-[12.5px] font-bold text-[var(--text-primary)]">Send this follow-up to yourself</div>
        <p className="m-0 mt-1 text-[11.5px] text-[var(--text-muted)]">A made-up patient. The payment link is a test link — nothing can be paid.</p>
        <div className="mt-2.5 flex gap-2"><RmButton>Send the first step</RmButton><RmButton kind="soft">Send every step</RmButton></div>
      </div>
      <div className="mt-4 rounded-[16px] border border-[var(--money-green)] bg-[var(--surface-card)] px-5 py-4" style={{ borderTopWidth: 3 }}>
        <div className="flex items-center gap-1.5 text-[13px] font-bold text-[var(--money-green)]"><Icon kind="check" size={14} stroke={3} />Every step is ready</div>
        <div className="mt-2 text-[12.5px] text-[var(--text-secondary)]">{AR_READBACK.match} patients · first texts go out the next sending window.</div>
      </div>
      <RmFooter>
        <RmButton onClick={onBack}>Back</RmButton>
        <RmButton kind="primary" onClick={onStart}>Start the follow-up</RmButton>
      </RmFooter>
    </>
  );
}
